import React, { useState } from 'react';
import {
  Box,
  Typography,
  Skeleton,
  Stack,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { ChevronLeft, Close, Add, Delete } from '@mui/icons-material';
import { useToast } from '@shared/hooks/useToast';
import { useGenericDrawer } from '@shared/shell/drawer/useGenericDrawer';
import { BorderedBlock } from '@shared/ui/common/BorderedBlock';
import { useCollection, useCollections } from '../hooks/useCollections';
import { useBottomSheet, BottomSheetSize } from '@shared/shell/bottomsheet/useBottomSheet';
import AddProductsToCollectionBottomSheet from './AddProductsToCollectionBottomSheet';
import ProductDetailsDrawer from '@features/products/components/detail/ProductDetailsDrawer';
import { DeleteDialog } from '@shared/ui/dialog/DeleteDialog';

interface CollectionDetailsProps {
  collectionId: string;
}

const CollectionDetails: React.FC<CollectionDetailsProps> = ({ collectionId }) => {
  const toast = useToast();
  const { onBack, onClose, openDrawer } = useGenericDrawer();
  const { openBottomSheet } = useBottomSheet();
  const { collection, isLoading } = useCollection(collectionId);
  const {
    removeProductsFromCollection,
    deleteCollection,
    addProductsToCollection,
    isDeleting,
  } = useCollections();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkRemoveOpen, setBulkRemoveOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const productCount = collection?.products?.length ?? 0;
  const allSelected = productCount > 0 && selectedIds.size === productCount;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const handleAddProducts = () => {
    openBottomSheet({
      title: 'Add Products to Collection',
      component: (
        <AddProductsToCollectionBottomSheet
          collectionId={collectionId}
          existingProductIds={new Set(collection?.products?.map(p => p.id) ?? [])}
          onClose={() => { }}
          onAdd={addProductsToCollection}
        />
      ),
      size: BottomSheetSize.MD,
    });
  };

  const toggleSelect = (productId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!collection?.products?.length) return;
    if (selectedIds.size === collection.products.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(collection.products.map(p => p.id)));
    }
  };

  const handleBulkRemove = async () => {
    const productIds = Array.from(selectedIds);
    if (productIds.length === 0) return;
    setIsRemoving(true);
    try {
      await removeProductsFromCollection({ collectionId, productIds });
      toast.success(
        `Removed ${productIds.length} product${productIds.length !== 1 ? 's' : ''} from collection`
      );
      setSelectedIds(new Set());
      setBulkRemoveOpen(false);
    } catch {
      toast.error('Failed to remove products');
    } finally {
      setIsRemoving(false);
    }
  };

  const handleDeleteCollection = async () => {
    try {
      await deleteCollection(collectionId);
      toast.success('Collection deleted');
      setDeleteDialogOpen(false);
      onBack?.() ?? onClose?.();
    } catch {
      toast.error('Failed to delete collection');
    }
  };

  const handleProductClick = (productId: string) => {
    openDrawer(
      'Product Details',
      <ProductDetailsDrawer productId={productId} />,
      () =>
        openDrawer(
          'Collection Details',
          <CollectionDetails collectionId={collectionId} />
        )
    );
  };

  return (
    <>
      <Stack
        direction="row"
        position="sticky"
        top={0}
        p={3}
        zIndex={1}
        bgcolor="background.paper"
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack direction="row" alignItems="center" spacing={0}>
          {onBack && (
            <IconButton onClick={onBack} edge="start">
              <ChevronLeft />
            </IconButton>
          )}
          <Typography variant="h6">Collection Details</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          {collection && (
            <Button color="error" size="small" onClick={() => setDeleteDialogOpen(true)}>
              Delete
            </Button>
          )}
          {onClose && (
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          )}
        </Stack>
      </Stack>

      <Stack gap={3} sx={{ flexGrow: 1, p: 3, pt: 0 }}>
        {isLoading ? (
          <Box sx={{ width: '100%' }}>
            <Skeleton variant="rectangular" height={120} sx={{ mb: 2 }} />
            <Skeleton variant="text" height={40} width="70%" />
          </Box>
        ) : !collection ? (
          <Box sx={{ textAlign: 'center', my: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Collection not found
            </Typography>
          </Box>
        ) : (
          <Stack sx={{ flexGrow: 1, overflow: 'auto', gap: 4 }}>
            <BorderedBlock>
              <Typography variant="subtitle1">{collection.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {productCount} product{productCount !== 1 ? 's' : ''}
              </Typography>
            </BorderedBlock>

            <BorderedBlock
              sx={{ p: 0 }}
              header={
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    Products
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    {selectedIds.size > 0 && (
                      <Button
                        color="error"
                        size="small"
                        variant="outlined"
                        startIcon={<Delete />}
                        onClick={() => setBulkRemoveOpen(true)}
                      >
                        Remove selected ({selectedIds.size})
                      </Button>
                    )}
                    <Button
                      startIcon={<Add />}
                      size="small"
                      variant="outlined"
                      onClick={handleAddProducts}
                    >
                      Add product
                    </Button>
                  </Stack>
                </Stack>
              }
            >
              <List disablePadding>
                {collection.products && collection.products.length > 0 ? (
                  <>
                    <ListItem
                      sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        py: 1,
                        px: 2,
                        bgcolor: 'action.hover',
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={toggleSelectAll}
                        inputProps={{ 'aria-label': 'Select all products' }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Select all
                      </Typography>
                    </ListItem>
                    {collection.products.map(product => (
                      <ListItem
                        key={product.id}
                        disablePadding
                        secondaryAction={
                          <Checkbox
                            edge="end"
                            tabIndex={-1}
                            checked={selectedIds.has(product.id)}
                            onChange={() => toggleSelect(product.id)}
                            onClick={e => e.stopPropagation()}
                            inputProps={{ 'aria-label': `Select ${product.title || 'product'}` }}
                          />
                        }
                      >
                        <ListItemButton onClick={() => handleProductClick(product.id)} sx={{ py: 2, pr: 6 }}>
                          <Avatar
                            src={product.image}
                            variant="rounded"
                            sx={{
                              width: 40,
                              height: 40,
                              border: 1,
                              borderColor: 'divider',
                              mr: 2,
                            }}
                          />
                          <Stack flexGrow={1} minWidth={0}>
                            <Typography variant="subtitle2" noWrap>
                              {product.title || 'Untitled'}
                            </Typography>
                            {product.ean && (
                              <Typography variant="caption" color="text.secondary">
                                EAN: {product.ean}
                              </Typography>
                            )}
                          </Stack>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </>
                ) : (
                  <ListItem>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <Typography variant="body2" color="text.secondary">
                        No products in this collection
                      </Typography>
                      <Button
                        startIcon={<Add />}
                        size="small"
                        variant="outlined"
                        onClick={handleAddProducts}
                      >
                        Add product
                      </Button>
                    </Stack>
                  </ListItem>
                )}
              </List>
            </BorderedBlock>
          </Stack>
        )}
      </Stack>

      <Dialog open={bulkRemoveOpen} onClose={() => !isRemoving && setBulkRemoveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Remove products from collection</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove {selectedIds.size} product{selectedIds.size !== 1 ? 's' : ''} from this collection? The products
            will stay in your catalog; only this link is removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkRemoveOpen(false)} disabled={isRemoving}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void handleBulkRemove()}
            disabled={isRemoving}
            startIcon={isRemoving ? <CircularProgress size={16} color="inherit" /> : <Delete />}
          >
            {isRemoving ? 'Removing…' : 'Remove from collection'}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteDialog
        open={deleteDialogOpen}
        title="Delete Collection"
        description="Are you sure you want to delete this collection? This action cannot be undone."
        cancelDelete={() => setDeleteDialogOpen(false)}
        confirmDelete={handleDeleteCollection}
        isDeleting={isDeleting}
      />
    </>
  );
};

export default CollectionDetails;
